import type { Role, TargetPayload } from '../shared/domain';
import type { SavedCriterion } from '../shared/lanes';
import type { ReportStatus } from '../shared/status';

const SESSION_KEY = 'mdrc-session-v1';

export interface SessionState {
  token: string;
  username: string;
}

export interface AuthUser {
  id: string;
  username: string;
  displayName: string;
  role: Role;
}

export interface Person {
  id: string;
  call_sign: string;
  rank: string;
  billet: string;
  element: string;
  edipi: string;
  age: string;
  blood_type: string;
  allergies: string;
  photo_key: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Spot {
  id: string;
  client_id: string;
  observed_at: string;
  author_id: string;
  author_name: string;
  element: string;
  elements: string[];
  targets: TargetPayload;
  location_lat: number | null;
  location_lng: number | null;
  location_accuracy_m: number | null;
  location_description: string;
  category: string;
  narrative: string;
  flash: boolean;
  status: ReportStatus;
  evidence_keys: string[];
  references: string[];
  created_at: string;
  updated_at: string;
  edited_by?: string[];
  approved_by?: string;
}

export interface Lane {
  id: string;
  client_id: string;
  lane_id: string;
  observed_at: string;
  author_id: string;
  author_name: string;
  element: string;
  elements: string[];
  targets: TargetPayload;
  location_lat: number | null;
  location_lng: number | null;
  location_accuracy_m: number | null;
  location_description: string;
  context: string;
  linked_spot_ids: string[];
  scores: Record<string, number | 'na'>;
  comments: Record<string, string>;
  criteria: { id: string; title: string; prompt: string; lookFor: string[] }[];
  strengths: string;
  deficiencies: string;
  remediation: string;
  narrative: string;
  excon_comments: string;
  status: ReportStatus;
  evidence_keys: string[];
  references: string[];
  created_at: string;
  updated_at: string;
  edited_by?: string[];
  approved_by?: string;
}

export interface Feed {
  spots: Spot[];
  lanes: Lane[];
}

export interface UserAccount {
  id: string;
  username: string;
  displayName: string;
  role: Role;
  active: boolean;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export function isOffline(err: unknown): boolean {
  return err instanceof ApiError && err.status === 0;
}

export function getSession(): SessionState | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as SessionState) : null;
  } catch {
    return null;
  }
}

export function setSession(session: SessionState | null): void {
  if (!session) localStorage.removeItem(SESSION_KEY);
  else localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const headers: Record<string, string> = { Accept: 'application/json', ...extra };
  const session = getSession();
  if (session?.token) headers['X-Session-Token'] = session.token;
  return headers;
}

async function parse<T>(res: Response, hadToken: boolean): Promise<T> {
  const body = (await res.json().catch(() => ({}))) as { error?: string; data?: T };
  if (!res.ok) {
    if (res.status === 401 && hadToken) {
      window.dispatchEvent(new CustomEvent('mdrc-unauthorized'));
    }
    throw new ApiError(body.error || `Request failed (${res.status})`, res.status);
  }
  return body as T;
}

export async function apiGet<T>(path: string): Promise<T> {
  const hadToken = !!getSession()?.token;
  let res: Response;
  try {
    res = await fetch(path, { headers: authHeaders(), credentials: 'same-origin' });
  } catch {
    throw new ApiError('No signal', 0);
  }
  return parse<T>(res, hadToken);
}

export async function apiSend<T>(path: string, method: string, body?: unknown): Promise<T> {
  const hadToken = !!getSession()?.token;
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError('No signal', 0);
  }
  return parse<T>(res, hadToken);
}

export async function login(username: string, password: string): Promise<AuthUser> {
  let res: Response;
  try {
    res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ username, password }),
    });
  } catch {
    throw new ApiError('No signal', 0);
  }
  const data = await parse<{ data: { token: string; user: AuthUser } }>(res, false);
  setSession({ token: data.data.token, username: data.data.user.username });
  return data.data.user;
}

export async function logout(): Promise<void> {
  try {
    await apiSend('/api/auth/logout', 'POST', {});
  } catch {
    /* still drop the local session */
  }
  setSession(null);
}

export async function fetchMe(): Promise<AuthUser> {
  const data = await apiGet<{ data: AuthUser }>('/api/me');
  return data.data;
}

export async function changePassword(current: string, next: string): Promise<void> {
  await apiSend('/api/auth/password', 'POST', {
    current_password: current,
    new_password: next,
  });
}

export async function fetchPeople(all = false): Promise<Person[]> {
  const data = await apiGet<{ data: Person[] }>(
    all ? '/api/personnel?all=1' : '/api/personnel'
  );
  return data.data;
}

export async function createPerson(body: {
  call_sign: string;
  billet: string;
  element: string;
  edipi?: string;
  age?: string;
  blood_type?: string;
  allergies?: string;
}): Promise<Person> {
  const data = await apiSend<{ data: Person }>('/api/personnel', 'POST', body);
  return data.data;
}

export async function patchPerson(
  id: string,
  body: Partial<{
    call_sign: string;
    billet: string;
    element: string;
    edipi: string;
    age: string;
    blood_type: string;
    allergies: string;
    active: boolean;
  }>
): Promise<Person> {
  const data = await apiSend<{ data: Person }>(`/api/personnel/${id}`, 'PATCH', body);
  return data.data;
}

export async function uploadPhoto(
  data: Blob,
  contentType: string,
  filename = ''
): Promise<string> {
  const hadToken = !!getSession()?.token;
  const headers = authHeaders({ 'Content-Type': contentType || 'application/octet-stream' });
  if (filename) headers['X-Filename'] = filename;
  let res: Response;
  try {
    res = await fetch('/api/evidence/upload', {
      method: 'POST',
      credentials: 'same-origin',
      headers,
      body: data,
    });
  } catch {
    throw new ApiError('No signal', 0);
  }
  const parsed = await parse<{ data: { key: string } }>(res, hadToken);
  return parsed.data.key;
}

export async function uploadRosterPhoto(id: string, data: Blob, contentType: string): Promise<Person> {
  const hadToken = !!getSession()?.token;
  let res: Response;
  try {
    res = await fetch(`/api/personnel/photo/${id}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: authHeaders({ 'Content-Type': contentType }),
      body: data,
    });
  } catch {
    throw new ApiError('No signal', 0);
  }
  const parsed = await parse<{ data: Person }>(res, hadToken);
  return parsed.data;
}

const photoUrls = new Map<string, string>();

export async function photoUrl(key: string): Promise<string> {
  const cached = photoUrls.get(key);
  if (cached) return cached;
  const hadToken = !!getSession()?.token;
  let res: Response;
  try {
    res = await fetch(
      `/api/evidence/${key.split('/').map(encodeURIComponent).join('/')}`,
      { headers: authHeaders({ Accept: '*/*' }), credentials: 'same-origin' }
    );
  } catch {
    throw new ApiError('No signal', 0);
  }
  if (!res.ok) {
    if (res.status === 401 && hadToken) {
      window.dispatchEvent(new CustomEvent('mdrc-unauthorized'));
    }
    throw new ApiError(`Photo failed (${res.status})`, res.status);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  photoUrls.set(key, url);
  return url;
}

export async function fetchLaneCriteria(): Promise<Record<string, SavedCriterion[]>> {
  const data = await apiGet<{ data: Record<string, SavedCriterion[]> }>('/api/lane-criteria');
  return data.data || {};
}

export async function saveLaneCriteria(
  laneId: string,
  criteria: SavedCriterion[]
): Promise<SavedCriterion[]> {
  const data = await apiSend<{ data: SavedCriterion[] }>(
    `/api/lane-criteria/${encodeURIComponent(laneId)}`,
    'PUT',
    { criteria }
  );
  return data.data;
}

export async function fetchFeed(): Promise<Feed> {
  const data = await apiGet<{ data: Feed }>('/api/feed');
  return data.data;
}

export async function fetchSpot(id: string): Promise<Spot> {
  const data = await apiGet<{ data: Spot }>(`/api/spots/${id}`);
  return data.data;
}

export async function saveSpot(body: Record<string, unknown>, id?: string): Promise<Spot> {
  if (id) {
    const data = await apiSend<{ data: Spot }>(`/api/spots/${id}`, 'PATCH', body);
    return data.data;
  }
  const data = await apiSend<{ data: Spot }>('/api/spots', 'POST', body);
  return data.data;
}

export async function fetchLane(id: string): Promise<Lane> {
  const data = await apiGet<{ data: Lane }>(`/api/lanes/${id}`);
  return data.data;
}

export interface Aar {
  id: string;
  client_id: string;
  observed_at: string;
  author_id: string;
  author_name: string;
  element: string;
  elements: string[];
  location_lat: number | null;
  location_lng: number | null;
  location_accuracy_m: number | null;
  location_description: string;
  topic: string;
  discussion: string;
  recommendation: string;
  status: ReportStatus;
  evidence_keys: string[];
  edited_by?: string[];
  approved_by?: string;
  created_at: string;
  updated_at: string;
}

export async function fetchAars(): Promise<Aar[]> {
  const data = await apiGet<{ data: Aar[] }>('/api/aars');
  return data.data || [];
}

export async function fetchAar(id: string): Promise<Aar> {
  const data = await apiGet<{ data: Aar }>(`/api/aars/${id}`);
  return data.data;
}

export async function saveAar(body: Record<string, unknown>, id?: string): Promise<Aar> {
  if (id) {
    const data = await apiSend<{ data: Aar }>(`/api/aars/${id}`, 'PATCH', body);
    return data.data;
  }
  const data = await apiSend<{ data: Aar }>('/api/aars', 'POST', body);
  return data.data;
}

export async function deleteAar(id: string): Promise<void> {
  await apiSend(`/api/aars/${id}`, 'DELETE');
}

export async function reviewReport(
  kind: 'spots' | 'lanes' | 'aars',
  id: string,
  review: 'approved' | 'returned'
): Promise<void> {
  await apiSend(`/api/${kind}/${id}`, 'PATCH', { review });
}

export async function deleteSpot(id: string): Promise<void> {
  await apiSend(`/api/spots/${id}`, 'DELETE');
}

export async function deleteLane(id: string): Promise<void> {
  await apiSend(`/api/lanes/${id}`, 'DELETE');
}

export async function downloadReportPdf(kind: 'spots' | 'lanes' | 'aars', id: string): Promise<void> {
  const hadToken = !!getSession()?.token;
  let res: Response;
  try {
    res = await fetch(`/api/${kind}/${id}?format=pdf`, {
      headers: authHeaders({ Accept: 'application/pdf' }),
      credentials: 'same-origin',
    });
  } catch {
    throw new ApiError('No signal', 0);
  }
  if (!res.ok) {
    if (res.status === 401 && hadToken) {
      window.dispatchEvent(new CustomEvent('mdrc-unauthorized'));
    }
    throw new ApiError(`Export failed (${res.status})`, res.status);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `MDRC-${kind === 'spots' ? 'spot' : kind === 'lanes' ? 'lane' : 'aar'}.pdf`;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export async function saveLane(body: Record<string, unknown>): Promise<Lane> {
  const data = await apiSend<{ data: Lane }>('/api/lanes', 'POST', body);
  return data.data;
}

export async function fetchUsers(): Promise<UserAccount[]> {
  const data = await apiGet<{ data: UserAccount[] }>('/api/admin/users');
  return data.data;
}

export async function createUser(body: {
  username: string;
  display_name: string;
  password: string;
  role: Role;
}): Promise<UserAccount> {
  const data = await apiSend<{ data: UserAccount }>('/api/admin/users', 'POST', body);
  return data.data;
}

export async function patchUser(
  id: string,
  body: Partial<{ display_name: string; role: Role; active: boolean; password: string }>
): Promise<UserAccount> {
  const data = await apiSend<{ data: UserAccount }>(`/api/admin/users/${id}`, 'PATCH', body);
  return data.data;
}

export async function downloadWorkbook(): Promise<void> {
  const hadToken = !!getSession()?.token;
  let res: Response;
  try {
    res = await fetch('/api/export', {
      headers: authHeaders({
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      credentials: 'same-origin',
    });
  } catch {
    throw new ApiError('No signal', 0);
  }
  if (!res.ok) {
    let message = `Export failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* binary or empty */
    }
    if (res.status === 401 && hadToken) {
      window.dispatchEvent(new CustomEvent('mdrc-unauthorized'));
    }
    throw new ApiError(message, res.status);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `MDRC-Evaluation-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
